import { findKey, has, indexOf, reject, some } from 'lodash-es';
import { appStore, assetMapping } from './helpers';

const valueStringify = (value) => value && value.toString();
const isSameValue = (value1, value2) => {
  if (!value1 || !value2) return false;
  return valueStringify(value1) === valueStringify(value2);
};
const isSameStore = (current, selected) => appStore(current.store) === appStore(selected.store);

const getEntityConflict = (current, selected) => {
  const selectedAsset = findKey(assetMapping, (kinds) => kinds.includes(selected.kind));

  switch (current.kind) {
    case 'app':
    case 'unified_app': {
      // appStoreConflict
      if (selectedAsset === 'apps') {
        const isConflict =
          current.cross_store_app_id && selected.cross_store_app_id
            ? isSameValue(current.cross_store_app_id, selected.cross_store_app_id) &&
              !isSameStore(current, selected)
            : isSameValue(current[`${selected.store}_ref_no`], selected.ref_no);
        return (
          isConflict && {
            type: 'appStoreConflict',
            message: {
              icon: 'hicon-warning',
              title: 'App Store Conflict',
              text: 'Some results contain store-specific or unified versions of already included entities. Adding these will replace the currently included version.',
            },
          }
        );
      }
      // parentEntityConflict
      if (selectedAsset === 'publishers') {
        const isConflict =
          isSameValue(current.publisher_id, selected.ref_no) ||
          isSameValue(current.cross_store_publisher_id, selected.cross_store_publisher_id);

        return (
          isConflict && {
            type: 'parentEntityConflict',
            message: {
              icon: 'hicon-warning',
              title: 'Parent Entity Conflict',
              text: 'Some results contain apps belonging to already selected publishers. Adding these apps will replace the currently included publisher of the app.',
            },
          }
        );
      }
      // parentEntityConflict
      if (selectedAsset === 'companies') {
        return (
          isSameValue(current.parent_company_id, selected.ref_no) && {
            type: 'parentEntityConflict',
            message: {
              icon: 'hicon-warning',
              title: 'Parent Entity Conflict',
              text: 'Some results contain apps belonging to already selected companies. Adding these apps will replace the currently included parent company of the app.',
            },
          }
        );
      }

      return null;
    }
    case 'publisher':
    case 'unified_publisher': {
      // childEntityConflict
      if (selectedAsset === 'apps') {
        const isConflict =
          isSameValue(current.ref_no, selected.publisher_id) ||
          isSameValue(current.cross_store_publisher_id, selected.cross_store_publisher_id);

        return (
          isConflict && {
            type: 'childEntityConflict',
            message: {
              icon: 'hicon-warning',
              title: 'Child Entity Conflict',
              text: 'Some results contain publishers of already selected apps. Adding the publisher will replace all currently included apps owned by the publisher.',
            },
          }
        );
      }
      // appStoreConflict
      if (selectedAsset === 'publishers') {
        const isConflict =
          current.cross_store_publisher_id && selected.cross_store_publisher_id
            ? isSameValue(current.cross_store_publisher_id, selected.cross_store_publisher_id) &&
              !isSameStore(current, selected)
            : isSameValue(
                current[`${selected.store}_ref_no`] || current[`${selected.store}_publisher_id`],
                selected.ref_no
              );

        return (
          isConflict && {
            type: 'appStoreConflict',
            message: {
              icon: 'hicon-warning',
              title: 'App Store Conflict',
              text: 'Some results contain store-specific or unified versions of already included entities. Adding these will replace the currently included version.',
            },
          }
        );
      }
      // parentEntityConflict
      if (selectedAsset === 'companies') {
        return (
          isSameValue(current.parent_company_id, selected.ref_no) && {
            type: 'parentEntityConflict',
            message: {
              icon: 'hicon-warning',
              title: 'Parent Entity Conflict',
              text: 'Some results contain publishers belonging to already selected companies. Adding these publishers will replace the currently included parent company of the publisher.',
            },
          }
        );
      }

      return null;
    }
    case 'company': {
      // childEntityConflict
      if (['apps', 'publishers'].includes(selectedAsset)) {
        return (
          isSameValue(current.ref_no, selected.parent_company_id) && {
            type: 'childEntityConflict',
            message: {
              icon: 'hicon-warning',
              title: 'Child Entity Conflict',
              text: 'Some results contain parent companies of already selected apps or publishers. Adding the company will replace all currently included apps and publishers owned by the company.',
            },
          }
        );
      }

      return false;
    }
    default:
      return false;
  }
};

const getReplaceData = (currentEntity, selectedEntities) => {
  const conflicts = new Map();

  const entitiesToReplace = selectedEntities.reduce((result, selectedEntity) => {
    const entityConflict = getEntityConflict(currentEntity, selectedEntity);

    if (entityConflict) {
      result.push(selectedEntity);
      conflicts.set(entityConflict.type, entityConflict);
    }

    return result;
  }, []);

  return { entitiesToReplace, conflicts: Array.from(conflicts.values()) };
};

export const enhanceEntity = (selectedEntities) => (entity) => {
  if (!selectedEntities || !selectedEntities.length) {
    return {
      itemSelected: false,
      showReplaceButton: false,
      conflicts: [],
    };
  }

  const isEntityAvailable = has(entity, 'available') ? entity.available : true;
  const isEntitySelected = some(selectedEntities, (selected) =>
    isSameValue(entity.ref_no, selected.ref_no)
  );

  let conflicts = [];

  if (isEntityAvailable && !isEntitySelected) {
    ({ conflicts } = getReplaceData(entity, selectedEntities));
  }

  return {
    itemSelected: isEntitySelected,
    conflicts,
    showReplaceButton: !!conflicts.length,
  };
};

export const replaceEntity = (entity, selectedEntities) => {
  const { entitiesToReplace } = getReplaceData(entity, selectedEntities);

  const entitiesRefNosToReplace = new Set(entitiesToReplace.map(({ ref_no }) => `${ref_no}`));
  const entitiesWithoutItemsToReplace = reject(selectedEntities, ({ ref_no }) =>
    entitiesRefNosToReplace.has(`${ref_no}`)
  );
  const indexOfEntitiesToReplace = indexOf(selectedEntities, entitiesToReplace[0]);

  return [
    ...entitiesWithoutItemsToReplace.slice(0, indexOfEntitiesToReplace),
    entity,
    ...entitiesWithoutItemsToReplace.slice(indexOfEntitiesToReplace),
  ];
};
